import React, { useMemo } from "react";
import TimeTrackingCellValue from "./TimeTrackingCellValue";
import { TimeTrackingCellTypedValue } from "./TimeTrackingCell";
import { TimeTrackingEntry, TimeTrackingLine } from "../../../store/TimeTrackingStore";
import { ITimelineSegment } from "../../common/timeline/TimelineSegment";
import { IRow } from "../../common/timeline/TimelineList";
import { EntityType } from "../../../entities/common";
import { FormatType } from "../../../entities/Metadata";

interface OwnProps {
    row: IRow;
    segment: ITimelineSegment;
    expected?: number;
    isDayOff: boolean;
    typedValues?: TimeTrackingCellTypedValue[];
}

type Props = OwnProps;

export default function TotalTimeTrackingCell(props: Props) {
    const { row, segment, expected, isDayOff, typedValues } = props;

    const entries = getTimeEntries(row, segment);

    const total = useMemo(() => {
        const reported = entries.reduce((sum, cur) => sum + cur.attributes.Duration!, 0);
        return reported;
    }, [row, segment]);

    const isExceeded = expected !== undefined && total > expected;

    const className = [
        'time-tracking-cell',
        'fill-space',
        'total',
        isExceeded ? 'exceeded' : undefined
    ].join(" ");

    return (
        <div className={className} style={{ position: "relative" }}>
            <div className={`cell-content fill-space columns-${typedValues?.length ?? 1}`}>
                {typedValues ? (
                    typedValues.map(_ => (
                        <TimeTrackingCellValue
                            key={_.type.key}
                            value={_.value}
                            title={_.type.title}
                            className={_.type.className}
                            unit={_.type.unit}
                            format={_.type.format}
                            emptyText={_.type.emptyText}
                            isDayOff={isDayOff}
                        />
                    ))
                ) : (
                    <TimeTrackingCellValue
                        value={total}
                        title="Reported"
                        className="reported"
                        expected={expected}
                        format={FormatType.Duration}
                        isDayOff={isDayOff}
                    />
                )}
            </div>
        </div>
    );
}

export const getTimeTrackinLines = (row: IRow): TimeTrackingLine[] => {
    const result: TimeTrackingLine[] = [];

    if (row.subItemType === EntityType.Task) {
        result.push(row.entity as TimeTrackingLine);
    } else if (row.subItems?.length) {
        row.subItems.forEach(_ => {
            const subLines = getTimeTrackinLines(_);
            result.push(...subLines);
        });
    }

    return result;
};

export const getTimeEntries = (row: IRow, segment: ITimelineSegment) => {
    const lines = getTimeTrackinLines(row);
    const entries: TimeTrackingEntry[] = lines.reduce((result: TimeTrackingEntry[], line) => {
        const lineEntries = line.TimeEntries.filter(
            _ => _.attributes.Date >= segment.startDate && _.attributes.Date <= segment.finishDate
        );
        result.push(...lineEntries);
        return result;
    }, []);

    return entries;
};
